import { GLOBAL_SIGNALS, SCREENS, screenSignals } from '../../lib/agent/discovery';
import { screenForPath } from '../../lib/agent/screens';
import { formatApiResult } from '../../lib/agent/apiResult';
import { agentRuntime } from './runtime';
import { currentAgentLocation } from './navigation';
import { debug } from '../../lib/debug';

/**
 * Agent signals: `REQUEST_RUN_ANALYSIS` / `REQUEST_*_SUBMIT`, handed to the
 * screen that owns the button.
 *
 * The Ki_UI way: the agent fills a form in the `agent` slice with `UPDATE_*`,
 * then fires a signal. The signal is recorded in the store and parked here as a
 * request for the screen that is OPEN; that page's `useAgentRunAnalysis` /
 * `useAgentSubmit` claims it, runs the same code its button runs (asking the
 * user first when it writes or costs), and settles it. Exactly one
 * `[api_result]` goes back per signal, whichever way it ends.
 *
 * Why a store of requests rather than Ki_UI's nonce + FIFO rendezvous:
 * - Correlation is by `requestId`. A FIFO of waiters let one flow's result
 *   resolve another flow's wait; here a settle can only ever close its own
 *   request, and a second settle for the same id is ignored.
 * - A request belongs to the screen that was open when it was fired. A page
 *   that mounts later for another screen cannot pick it up, and the signal the
 *   agent sent WITH a navigate is fired after the navigate settles, so it is
 *   parked for the screen it was meant for.
 * - The page often mounts AFTER the request exists. A counter bumped before
 *   mount is already inside the new page's baseline and is lost; a pending
 *   request is simply claimed on mount.
 * - A request nobody claims is failed after 20 s with a reason the agent can
 *   act on, instead of waiting forever for a handler that is not coming.
 */

/** A request must be picked up by its screen within this long of being parked. */
const CLAIM_TIMEOUT_MS = 20000;
/**
 * Hard cap on a claimed request. Long enough for a full risk sweep and a
 * two-minute confirmation; a handler that never settles must still not leave
 * the agent (or this map) waiting forever.
 */
const RESULT_TIMEOUT_MS = 10 * 60 * 1000;

/** What a run card calls each signal, after the screen's name. */
const SIGNAL_LABELS = {
  REQUEST_RUN_ANALYSIS: 'run',
  REQUEST_CLIENT_SUBMIT: 'add client',
  REQUEST_PORTFOLIO_SUBMIT: 'create portfolio',
  REQUEST_STOCK_FEEDBACK_SUBMIT: 'send feedback',
  REQUEST_STOCK_VERIFY_SUBMIT: 'verify report',
  REQUEST_EKYC_SUBMIT: 'send eKYC invite',
  REQUEST_KYC_SECTIONS_SUBMIT: 'save KYC sections',
  REQUEST_KYC_FINAL_SUBMIT: 'file KYC',
  REQUEST_WATCHLIST_SUBMIT: 'update watchlist',
  REQUEST_PROPOSAL_SUBMIT: 'save proposal',
};

/** requestId → entry. A Map keeps insertion order, which is what "oldest first" claims rely on. */
const pending = new Map();
const subscribers = new Set();

const requestIdOf = () => `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** The public view of an entry: no timers, no card bookkeeping callers could mutate. */
const snapshot = (entry) => ({
  requestId: entry.requestId,
  type: entry.type,
  key: entry.key,
  title: entry.card.title,
  cardId: entry.cardId,
  createdAt: entry.createdAt,
  claimed: entry.claimed,
  claimedAt: entry.claimedAt,
});

/**
 * Tell subscribers something changed. Each is isolated: one page's broken
 * handler must not stop the page that actually owns the request from hearing.
 */
function notify(type, entry) {
  const event = { type, request: snapshot(entry) };
  [...subscribers].forEach((fn) => {
    try {
      fn(event);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.warn('[agent] request subscriber threw', err);
    }
  });
}

/**
 * The run card, rewritten whole on every change. Sending the full card rather
 * than a partial patch means the result never depends on how deeply the
 * provider merges.
 */
function writeCard(entry, status, detail) {
  entry.card = { ...entry.card, status };
  if (detail != null && detail !== '') entry.card.detail = String(detail);
  else delete entry.card.detail;
  agentRuntime().updateMessage(entry.cardId, { card: { ...entry.card } });
}

function clearTimers(entry) {
  if (entry.claimTimer) clearTimeout(entry.claimTimer);
  if (entry.resultTimer) clearTimeout(entry.resultTimer);
  entry.claimTimer = null;
  entry.resultTimer = null;
}

/**
 * Close a request: remove it, update its card, send its one report.
 * Removing first is what makes a late second settle (a timer racing the page) a no-op.
 */
function finish(entry, { status, detail, text }) {
  if (pending.get(entry.requestId) !== entry) return false;
  pending.delete(entry.requestId);
  clearTimers(entry);
  writeCard(entry, status, detail);
  if (text) agentRuntime().report(text);
  debug('agent → request settled', { requestId: entry.requestId, type: entry.type, status, detail });
  notify('settled', entry);
  return true;
}

function armClaimTimeout(entry) {
  if (entry.claimTimer) clearTimeout(entry.claimTimer);
  entry.claimTimer = setTimeout(() => {
    entry.claimTimer = null;
    if (entry.claimed) return;
    const error = entry.key == null
      ? 'the app did not pick up the request'
      : `the ${entry.screen} screen did not pick up the request`;
    finish(entry, {
      status: 'failed',
      detail: 'The screen did not pick this up.',
      text: formatApiResult({ op: entry.type, ok: false, error }),
    });
  }, CLAIM_TIMEOUT_MS);
}

/**
 * Fire a signal on the agent's behalf.
 *
 * 1. Recorded in the store (`agent.lastSignal`), with the time in `meta.at`.
 * 2. Refused with one report when the open screen does not declare it (and it
 *    is not global): the agent is told to navigate somewhere that lists it.
 * 3. A run card shows the user what the agent started, and the request is
 *    parked for the open screen (or for the shell, when global).
 * 4. Nobody claims it within 20 s → the card fails and one report says so.
 *
 * Never throws, and reports at most once before the request is parked; after
 * that, its settle (or its timeout) is the one report.
 */
export async function startAgentSignal(type) {
  const runtime = agentRuntime();
  const op = typeof type === 'string' && type.trim() ? type.trim() : 'request';
  try {
    runtime.store.dispatch({ type: op, meta: { at: Date.now() } });

    const { pathname } = currentAgentLocation();
    const hereKey = pathname ? screenForPath(pathname) : null;
    const screen = SCREENS[hereKey]?.screen || 'this screen';
    const isGlobal = GLOBAL_SIGNALS.includes(op);
    if (!isGlobal && !screenSignals(hereKey).includes(op)) {
      runtime.report(formatApiResult({
        op,
        ok: false,
        error: `${op} is not available on ${screen} — navigate to a screen that lists it`,
      }));
      return;
    }

    const label = SIGNAL_LABELS[op] || op.toLowerCase();
    const entry = {
      requestId: requestIdOf(),
      type: op,
      key: isGlobal ? null : hereKey,
      screen,
      cardId: null,
      /* A global signal belongs to no screen, so its label stands alone. */
      card: { title: isGlobal ? `${label[0].toUpperCase()}${label.slice(1)}` : `${screen} · ${label}`, status: 'waiting' },
      createdAt: Date.now(),
      claimed: false,
      claimedAt: null,
      claimTimer: null,
      resultTimer: null,
    };
    entry.cardId = runtime.addMessage({ from: 'sys', kind: 'run', card: { ...entry.card } });

    /* Parked where the page can claim it; a page already mounted claims synchronously. */
    pending.set(entry.requestId, entry);
    armClaimTimeout(entry);
    debug('agent → request pending', { requestId: entry.requestId, type: op, key: entry.key });
    notify('pending', entry);
  } catch (err) {
    runtime.report(formatApiResult({ op, ok: false, error: `client error — ${err?.message || String(err)}` }));
  }
}

/** Listen for request changes: `fn({ type: 'pending'|'claimed'|'released'|'settled', request })`. */
export function subscribeAgentRequests(fn) {
  if (typeof fn !== 'function') return () => {};
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

/**
 * Take the oldest unclaimed request for `type` parked for screen `key` (null
 * for a global signal), or null. The card turns to "running" and the claim
 * deadline gives way to the result cap.
 */
export function claimAgentRequest(type, key) {
  const wantKey = key == null ? null : key;
  let found = null;
  for (const entry of pending.values()) {
    if (entry.type === type && entry.key === wantKey && !entry.claimed) {
      found = entry;
      break;
    }
  }
  if (!found) return null;
  found.claimed = true;
  found.claimedAt = Date.now();
  if (found.claimTimer) clearTimeout(found.claimTimer);
  found.claimTimer = null;
  found.resultTimer = setTimeout(() => {
    found.resultTimer = null;
    finish(found, {
      status: 'failed',
      detail: 'This took too long and was abandoned.',
      text: formatApiResult({ op: found.type, ok: false, error: `${found.card.title} did not finish within 10 minutes` }),
    });
  }, RESULT_TIMEOUT_MS);
  writeCard(found, 'running');
  notify('claimed', found);
  return snapshot(found);
}

/**
 * Show a claimed request's progress on its card: 'confirm' while the user is
 * being asked, 'running' once the work starts. Settling is what ends it.
 */
export function markAgentRequest(requestId, status) {
  const entry = pending.get(requestId);
  if (!entry || !entry.claimed || (status !== 'confirm' && status !== 'running')) return false;
  writeCard(entry, status);
  return true;
}

/**
 * Hand a claimed request back, unrun — for a page that unmounts between its
 * claim and the handler starting. React StrictMode does exactly that on every
 * mount in development, and without this the request would sit claimed by a
 * handler that no longer exists until the result cap.
 */
export function releaseAgentRequest(requestId) {
  const entry = pending.get(requestId);
  if (!entry || !entry.claimed) return false;
  entry.claimed = false;
  entry.claimedAt = null;
  if (entry.resultTimer) clearTimeout(entry.resultTimer);
  entry.resultTimer = null;
  writeCard(entry, 'waiting');
  armClaimTimeout(entry);
  notify('released', entry);
  /* Announced as pending again so a handler that is (re)mounted can claim it. */
  notify('pending', entry);
  return true;
}

/**
 * Close a request with the page's result and send its one report.
 *
 * `result`: `{ ok: true, op, detail?, data?, queued?, title? }` |
 * `{ ok: false, op, error, cancelled?, title? }` | `{ noop: true, op?, reason }`.
 * `op` names the operation in the report (`runMfPmReview`), defaulting to the
 * signal; `title` renames the run card once the page knows what it ran. A
 * no-op is reported as a failure with its reason, so the agent does not tell
 * the user something ran. Anything else counts as a failure with no result.
 * Returns false when the request is unknown or already settled.
 */
/**
 * A browser network failure, said as one.
 *
 * `fetch` rejects with "Failed to fetch" (Chrome), "NetworkError when attempting
 * to fetch resource." (Firefox) or "Load failed" (Safari) when the request never
 * got an answer — a dropped tunnel, the backend restarting. Relayed raw, the
 * agent tells the user the run "failed to fetch" as if the analysis itself had
 * broken. Nothing is retried here: a write may have landed before the drop.
 */
const NETWORK_FAILURE = /^(Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed|Network request failed)$/i;
const networkWording = (message) => (NETWORK_FAILURE.test(String(message).trim())
  ? `could not reach the backend (network error: ${String(message).trim()}) — likely a brief connection drop; asking again usually works`
  : message);

export function settleAgentRequest(requestId, result) {
  const entry = pending.get(requestId);
  if (!entry) {
    debug('agent → settle ignored (unknown or already settled)', { requestId });
    return false;
  }
  const r = result && typeof result === 'object' ? result : { ok: false, error: 'the screen returned no result' };
  const op = typeof r.op === 'string' && r.op.trim() ? r.op.trim() : entry.type;
  if (typeof r.title === 'string' && r.title.trim()) entry.card = { ...entry.card, title: r.title.trim() };

  if (r.noop) {
    const reason = String(r.reason || r.error || 'there was nothing to run');
    return finish(entry, { status: 'failed', detail: reason, text: formatApiResult({ op, ok: false, error: reason }) });
  }

  if (r.ok) {
    const queued = Boolean(r.queued);
    return finish(entry, {
      status: queued ? 'queued' : 'done',
      detail: r.detail,
      text: formatApiResult({
        op, ok: true, detail: r.detail, data: r.data, queued,
      }),
    });
  }

  const error = networkWording(r.error ? String(r.error?.message || r.error) : 'unknown error');
  return finish(entry, {
    status: r.cancelled ? 'cancelled' : 'failed',
    detail: error,
    text: formatApiResult({ op, ok: false, error }),
  });
}

/** Every open request, oldest first — for devtools. */
export function pendingAgentRequests() {
  return [...pending.values()].map(snapshot);
}

/**
 * Drop every open request without reporting — for the provider's teardown on
 * sign-out or an account change, so a request made in one conversation cannot
 * time out and report into the next one.
 */
export function cancelAgentRequests() {
  [...pending.values()].forEach((entry) => {
    pending.delete(entry.requestId);
    clearTimers(entry);
    writeCard(entry, 'cancelled');
    notify('settled', entry);
  });
}
